import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { LuGift } from 'react-icons/lu'

import type { UsageGrant } from '../models/useUsage'
import { liveBoosts, soonestExpiry, boostPhrase } from '../utilities/grants'

// UsageBoost tells a customer why their allowance is bigger this month than the plan they bought
// (app#171).
//
// ⚠⚠ WE CAN NOW CREATE THAT STATE AND NOTHING SHOWED IT. router#1057 gave support one primitive
// for a reset, a promotion and fleet-wide compensation, each an entry on the usage ledger with an
// expiry — so a customer's meters can read against a larger number than their plan advertises,
// with no explanation anywhere on the page. An unexplained allowance is a support ticket we
// created on purpose.
//
// ⚠ It renders NOTHING when there is nothing to say: no grants, a past period, a spent grant, an
// expired one. Absent, never a greyed-out box.

// ⚠⚠ UTC, for the reason utilities/freshness.ts states on the reset date. A grant's expiry is a
// server-side instant and the common one is midnight UTC at a period end; formatted in the
// reader's zone, everyone west of Greenwich is told the boost ends a DAY EARLY.
// ⚠ Found while building app#170, against the branch this one is stacked on.
const endsOn = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' })

type Props = { grants: UsageGrant[]; period: string; currentPeriod: string }

export default function UsageBoost({ grants, period, currentPeriod }: Readonly<Props>) {
  const boosts = liveBoosts(grants, period, currentPeriod)
  // ⚠⚠ WHAT IS LEFT, AND THE SENTENCE SAYS "LEFT" (app#446). `liveBoosts` carries `remaining`,
  // the ledger's figure. The sentence said "we added" before it, so on 2026-10-02 an org with
  // the 200 GB and $10.00 launch promotion read "we added 134.9 GB bandwidth and $9.54 compute":
  // a figure that became smaller with each byte used.
  const left = boostPhrase(boosts)
  if (!left) return null

  // ⚠ The date names the FIRST grant to end, not the last: promising the larger allowance for
  // longer than it exists is the one thing a notice like this can get wrong that the meters
  // cannot correct.
  const until = soonestExpiry(boosts)

  return (
    <Alert severity="info" icon={<LuGift size={18} />} sx={{ mb: 2 }}>
      <Typography variant="body2">
        Your allowance is larger this month — you have <strong>{left}</strong> of extra allowance left
        {until ? `, through ${endsOn(until)}` : ''}.
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {until
          ? 'Your plan itself has not changed. When the extra allowance ends, your usual included amounts apply.'
          : 'Your plan itself has not changed. This extra allowance has no end date.'}
      </Typography>
    </Alert>
  )
}
